// @ts-nocheck
import React, { useState, useEffect } from "react";
import { Skeleton } from "../Skeleton";
import { generateClient } from "aws-amplify/api";
import { listProcessedModelSchemasWithOutputs } from "../../graphql/customQueries";
import type { DataSourceFormState } from "../../types/composedForecast";
import { isForecastFilesOutput } from "../../utils/seriesSources";

import Select from "../ReusableComponents/Select";
const client = generateClient();

interface ProcessedModelOutputConfigProps {
  dataSource: DataSourceFormState;
  onUpdate: (updated: DataSourceFormState) => void;
}

interface OutputParam {
  id: string;
  name: string;
  description?: string;
  unit?: string;
  outputLocation?: string;
  active?: boolean;
  schemaId: string;
  storageMode?: string | null;
}

// A HISTORICAL model's output (DATASET) has no forecast issues: its rows live in the dataset it writes, which is
// picked as a Dataset source instead
const isForecastOutput = (o: OutputParam) => o.active !== false && isForecastFilesOutput(o);

interface Schema {
  id: string;
  name: string;
  description?: string;
  type: string;
  schemaType: string;
  isActive: boolean;
  outputs: { items: OutputParam[] };
}

const ProcessedModelOutputConfig: React.FC<ProcessedModelOutputConfigProps> = ({
  dataSource,
  onUpdate,
}) => {
  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSchemas();
  }, []);

  const loadSchemas = async () => {
    try {
      setLoading(true);
      setError(null);
      // Paused (inactive) models are listed too: inactive only stops their schedule, and their forecast
      // history — e.g. a migrated V1 model that never runs — is still worth charting and scoring.
      const { data } = await client.graphql({
        query: listProcessedModelSchemasWithOutputs,
      });
      setSchemas(data.listProcessedModelSchemas.items || []);
    } catch (err) {
      console.error("Error loading schemas:", err);
      setError("Failed to load model schemas");
    } finally {
      setLoading(false);
    }
  };

  const handleOutputSelect = (outputId: string) => {
    // Find the selected output across all schemas
    for (const schema of schemas) {
      const output = schema.outputs?.items?.find((o) => o.id === outputId);
      if (output) {
        onUpdate({
          ...dataSource,
          processedModelOutputId: output.id,
          processedModelOutputName: output.name,
          schemaName: schema.name,
          // A new output has its own variables; a choice made for the old one doesn't carry over
          variation: output.id === dataSource.processedModelOutputId ? dataSource.variation : undefined,
          sourceName: dataSource.sourceName || `${schema.name} - ${output.name}`,
        });
        return;
      }
    }
  };

  if (loading) {
    return (
      // Field-shaped placeholder: the select lands in exactly this box once the options load
      <Skeleton className="h-eai-control-lg w-full" radius="md" />
    );
  }

  if (error) {
    return (
      <div className="py-2 font-inter text-eai-body-sm text-eai-danger-text">
        {error}{" "}
        <button onClick={loadSchemas} className="underline">
          Retry
        </button>
      </div>
    );
  }

  // Filter schemas that have active outputs
  const schemasWithOutputs = schemas.filter(
    (s) => s.outputs?.items?.some(isForecastOutput)
  );

  return (
    <div className="space-y-3">
      <div>
        <label className="eai-meta-label mb-2 block">
          Output Parameter
        </label>
        <Select
          value={dataSource.processedModelOutputId || ""}
          onChange={(e) => handleOutputSelect(e.target.value)}
          containerClassName="w-full"
        >
          <option value="">Select an output parameter...</option>
          {schemasWithOutputs.map((schema) => (
            <optgroup
              key={schema.id}
              label={`${schema.name} (${schema.type})${schema.isActive === false ? " — paused" : ""}`}
            >
              {schema.outputs.items
                .filter(isForecastOutput)
                .map((output) => (
                  <option key={output.id} value={output.id}>
                    {output.name}
                    {output.unit ? ` (${output.unit})` : ""}
                    {output.description ? ` - ${output.description}` : ""}
                  </option>
                ))}
            </optgroup>
          ))}
        </Select>
        {/* The group a pick came from only shows while the list is open, so it stays named once closed */}
        {dataSource.schemaName && (
          <p className="mt-1 font-inter text-eai-body-xs text-eai-ink-faint">From {dataSource.schemaName}</p>
        )}
      </div>


      {schemasWithOutputs.length === 0 && !loading && (
        <p className="font-inter text-eai-body-xs text-eai-ink-faint">
          No model schemas with output parameters found.
        </p>
      )}
    </div>
  );
};

export default ProcessedModelOutputConfig;
